import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, X } from 'lucide-react';
import { DESKTOP_DOWNLOAD_PAGE, DESKTOP_LATEST_VERSION_JSON } from '@/desktop/constants';
import { openExternalUrl } from '@/desktop/open-external';
import { getElectronAPI } from '@/utils/platform';
import { LocalStorage } from '@/services/storage/local-storage';

function parseSemver(v: string): number[] {
  return v
    .replace(/^v/i, '')
    .split('.')
    .map((n) => parseInt(n, 10) || 0);
}

function isNewerVersion(latest: string, current: string): boolean {
  const a = parseSemver(latest);
  const b = parseSemver(current);
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i += 1) {
    const av = a[i] ?? 0;
    const bv = b[i] ?? 0;
    if (av > bv) return true;
    if (av < bv) return false;
  }
  return false;
}

const DISMISS_KEY = 'desktop_update_banner_dismissed_version';

export function DesktopUpdateBanner() {
  const { t } = useTranslation('common');
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [currentVersion, setCurrentVersion] = useState('');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    void (async () => {
      const api = getElectronAPI();
      if (api?.getAppVersion) {
        const v = await api.getAppVersion();
        setCurrentVersion(v);
      } else if (api?.appVersion) {
        setCurrentVersion(api.appVersion);
      }
    })();

    void (async () => {
      try {
        const res = await fetch(DESKTOP_LATEST_VERSION_JSON, { cache: 'no-store' });
        if (!res.ok) return;
        const data = (await res.json()) as { version?: string };
        if (data.version) setLatestVersion(data.version);
      } catch {
        // offline or missing manifest
      }
    })();
  }, []);

  useEffect(() => {
    if (!latestVersion) return;
    setDismissed(LocalStorage.get<string>(DISMISS_KEY) === latestVersion);
  }, [latestVersion]);

  if (!latestVersion || !currentVersion || dismissed) return null;
  if (!isNewerVersion(latestVersion, currentVersion)) return null;

  return (
    <div
      role="status"
      className="fixed bottom-4 left-1/2 z-[300] flex w-[min(420px,calc(100vw-2rem))] -translate-x-1/2 items-start gap-3 rounded-xl border border-primary/30 bg-card px-4 py-3 shadow-lg"
    >
      <Download className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
      <div className="min-w-0 flex-1 space-y-2">
        <p className="text-sm font-semibold text-foreground">{t('shared.desktop.updateAvailableTitle')}</p>
        <p className="text-xs text-muted-foreground">
          {t('shared.desktop.updateAvailableBody', { current: currentVersion, latest: latestVersion })}
        </p>
        <button
          type="button"
          className="inline-flex rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-95"
          onClick={() => void openExternalUrl(DESKTOP_DOWNLOAD_PAGE)}
        >
          {t('shared.desktop.updateCta')}
        </button>
      </div>
      <button
        type="button"
        className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-label={t('shared.desktop.dismissUpdate')}
        onClick={() => {
          LocalStorage.set(DISMISS_KEY, latestVersion);
          setDismissed(true);
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
